/* eslint-disable max-lines */

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Button from '@App/components/Button/Button';
import { useTranslation } from '@utils/i18n/i18n';

import './inactivity-modal.css';

const INACTIVITY_TIMEOUT_MS = 60 * 1000;
const COUNTDOWN_SECONDS = 20;
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'wheel'];

function isWelcomePath(pathname, isProfileFormOpen) {
  return /^\/tablet\/[^/]+\/?$/.test(pathname) && !isProfileFormOpen;
}

function isMonitorPath(pathname) {
  return pathname === '/monitor' || pathname.startsWith('/monitor/');
}

function getStartPath(pathname) {
  const tabletPath = pathname.match(/^\/tablet\/([^/]+)/);

  if (tabletPath) return `/tablet/${tabletPath[1]}`;
  if (pathname.startsWith('/monitor')) return '/monitor';

  return '/';
}

function InactivityModal() {
  const { pathname, state: locationState } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isProfileFormOpen = locationState?.isProfileFormOpen === true;
  const [isOpen, setIsOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [resetSignal, setResetSignal] = useState(0);
  const idleTimerRef = useRef(null);
  const isOpenRef = useRef(false);

  useEffect(() => {
    const clearIdleTimer = () => {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };

    const startIdleTimer = () => {
      if (isOpenRef.current) return;

      clearIdleTimer();
      idleTimerRef.current = window.setTimeout(() => {
        idleTimerRef.current = null;
        isOpenRef.current = true;
        setSecondsLeft(COUNTDOWN_SECONDS);
        setIsOpen(true);
      }, INACTIVITY_TIMEOUT_MS);
    };

    if (isMonitorPath(pathname) || isWelcomePath(pathname, isProfileFormOpen)) {
      clearIdleTimer();
      isOpenRef.current = false;
      setIsOpen(false);
      return undefined;
    }

    startIdleTimer();

    const handleActivity = () => {
      if (!isOpenRef.current) startIdleTimer();
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity);
    });

    return () => {
      clearIdleTimer();
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
    };
  }, [isProfileFormOpen, pathname, resetSignal]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const countdownTimer = window.setInterval(() => {
      setSecondsLeft(currentSeconds => currentSeconds - 1);
    }, 1000);

    return () => window.clearInterval(countdownTimer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || secondsLeft > 0) return;

    isOpenRef.current = false;
    setIsOpen(false);
    setResetSignal(signal => signal + 1);
    navigate(getStartPath(pathname), { replace: true });
  }, [isOpen, navigate, pathname, secondsLeft]);

  if (!isOpen) return null;

  const continueSession = () => {
    isOpenRef.current = false;
    setIsOpen(false);
    setSecondsLeft(COUNTDOWN_SECONDS);
    setResetSignal(signal => signal + 1);
  };

  const goToStartScreen = () => {
    isOpenRef.current = false;
    setIsOpen(false);
    setResetSignal(signal => signal + 1);
    navigate(getStartPath(pathname), { replace: true });
  };

  return (
    <div className="inactivity-modal" role="dialog" aria-modal="true" aria-labelledby="inactivity-modal-title">
      <div className="inactivity-modal__panel">
        <p className="inactivity-modal__eyebrow">{t('inactivity-eyebrow')}</p>
        <h2 id="inactivity-modal-title" className="inactivity-modal__title">
          {t('inactivity-title')}
        </h2>
        <p className="inactivity-modal__message">
          {t('inactivity-message', { seconds: secondsLeft })}
        </p>
        <div
          className="inactivity-modal__countdown"
          role="progressbar"
          aria-label={t('inactivity-countdown-label')}
          aria-valuemin="0"
          aria-valuemax={COUNTDOWN_SECONDS}
          aria-valuenow={secondsLeft}
        >
          <span
            className="inactivity-modal__countdown-fill"
            style={{ width: `${(secondsLeft / COUNTDOWN_SECONDS) * 100}%` }}
          />
        </div>
        <div className="inactivity-modal__actions">
          <Button type="button" onClick={continueSession}>
            {t('inactivity-continue')}
          </Button>
          <Button
            type="button"
            className="button inactivity-modal__start-button"
            onClick={goToStartScreen}
          >
            {t('inactivity-start-screen')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default InactivityModal;